/**
 * The indexable-URL inventory — SEO invariant `I0`.
 *
 * This module is the single answer to "which URLs does this site intend search
 * engines and answer engines to index?". Every SEO invariant that says "every
 * route" means every entry in {@link INDEXABLE_URLS}.
 *
 * Built from **one authoritative source per URL class**, never a union of the
 * several route declarations that exist in the repository:
 *
 * - static routes — {@link STATIC_ROUTE_PATHS}, declared here;
 * - blog posts — the `slug` of every entry in `src/data/blogPosts.ts`;
 * - case studies — the `slug` of every entry in `src/data/caseStudies.ts`.
 *
 * **This list was not invented here — it was re-homed.** `src/lib/faqResolver.ts`
 * composed the identical 49 paths to decide which routes get an FAQ block, which
 * made the static list hand-maintained four times: both build generators,
 * `src/App.tsx`, and that module. It now re-exports from here instead, so the
 * three copies that remain — `App.tsx` and the two generators — are exactly the
 * set invariant `I1` (step `E5.8`) exists to bind against this module. Adding a
 * static route therefore still takes four edits — plus **three** counts: the
 * two in `routeInventory.test.ts`, which say so when they fail, and the bare
 * `251` in `tests-node/titleBudget.test.ts`, which does not. That third one is
 * why this sentence is worth re-reading: it said *two* until CWS-139 added a
 * route and found it, and it said `245` until CWS-51 added the root. What
 * changed with CWS-24 is that one of the four
 * declarations owns the answer. "Indexed" in `faqResolver` and "indexable" here denote
 * the same set by construction; the two names are historical, not a distinction.
 * `.system_design/SYSTEM_DESIGN.md` §12a is the full account.
 *
 * Deliberately **excluded**, because they are not content a crawler should
 * index: the `/post/*` and `/the-ai-compass-newsletter` redirect routes, the
 * `/:lang/*` wildcard that renders `NotFound`, and the top-level `404.html`
 * that step `E3.4` will add. `404.html` cannot enter this inventory by
 * construction — it is a `public/` static copied verbatim that never passes
 * through `scripts/generate-static-meta.mjs` — so it is named here as a marker
 * for the next reader, not guarded by a test.
 *
 * Also indexed, by decision **D3** (2026-09-08, `.system_design/SEO.md` §10):
 * the bare root document `/` — the prerendered English home (CWS-237, `D20`,
 * was the language selector under CWS-51 / `D3`). It is excluded
 * provisionally until D3 answered; {@link ROOT_INDEXABLE_URL} carries it as a
 * **singleton**, because `/` is not a per-language path: the ×5 fan-out stays
 * 50 paths, {@link STATIC_ROUTE_PATHS}'s 18 entries stay put, and the total
 * indexable URL count is the fan-out (250) **plus the root document** (251).
 *
 * The root's locale is `"en"`, not null. The root document is English-authored
 * (its `html lang`, `og:locale` and copy are English), and a nullable locale
 * would hide the root from the within-locale uniqueness sweeps — the exact
 * "group of one" that `seoHead.test.ts`'s locale-coverage assertion refuses.
 * Consumers keying `(locale, routePath)` — the per-locale URL of a route —
 * must read {@link LOCALIZED_INDEXABLE_URLS}, where that key is unique by
 * construction; the root is a second page at the same route path only in the
 * sense that the root and the locale home are different documents.
 *
 * The exclusion removes the bare origin only: the per-locale home pages `/en`,
 * `/de`, `/fr`, `/it`, `/es` are the `""` entry in {@link STATIC_ROUTE_PATHS}
 * and *are* indexable. And `public/llms.txt:9` advertises the bare origin to AI
 * crawlers as the site's `Website` field — under D3's answer that line is
 * **correct as written**, which is why the question was put before the
 * inventory was frozen rather than after.
 *
 * **Paths, not absolute URLs, and no trailing slash.** This module deferred the
 * origin and the trailing-slash form to `B3`/`I3a`, step `E3.2`; **CWS-48
 * answered it and chose the slashless form these paths already had**, so no
 * entry changes. The origin now lives in `./urlNormalise`, which turns a path
 * here into the published URL (`toCanonicalUrl`) and into the build-output file
 * Cloudflare serves at it (`toOutputFilePath` — a flat `.html`, not a folder
 * index). A consumer asserting over build output calls `toOutputFilePath`
 * rather than composing a path; `.system_design/SYSTEM_DESIGN.md` §11.3 says
 * why that name is load-bearing.
 *
 * **Imports here are relative, not `@/`-aliased, and that is deliberate.** The
 * two build generators transpile `src/**` TypeScript with an ad-hoc loader that
 * rewrites **relative specifiers only** (`CLAUDE.md`; this is why
 * `src/data/blogPosts.ts` imports its side files as `"./…"`). `E5.8` must be
 * able to import this module from those scripts to prove `I1`; an `@/` import
 * added here would break `npm run build` while Vite and Vitest stayed green.
 *
 * **Two modules import this in the application graph — `faqResolver.ts` and
 * `components/seo/JsonLd.tsx` — and both take the path arrays only.** Never
 * import {@link INDEXABLE_URLS} from anything a component can reach: that is 251
 * objects shipped to every visitor. Today it is tree-shaken out — verified
 * against a real build, `dist/assets/*.js` contains no `urlPath` and no
 * locale-prefixed URL — but that is Rollup's doing, not a guard. (Note the
 * `*.js`: production source maps are on, so a `.js.map` does embed this file's
 * source text, docstring included. A `grep -r` over `dist/assets/` matches and
 * always has; the claim is about executed bytes.) `JsonLd.tsx` joined in
 * CWS-103, which needs {@link INDEXABLE_ROUTE_PATHS} to decide whether a
 * breadcrumb rung names a page that exists — `SEO.md` `B17`, invariant `S21b`.
 * It added no bytes: `faqResolver` already pulled this module into the eager
 * graph via `LanguageRouter`. {@link STATIC_ROUTE_PATHS} does ship, in six chunks; it shipped
 * before this module existed too, from `faqResolver`'s own copy, so re-homing
 * the list added no bytes.
 *
 * **No route classification is carried here.** `src/lib/faqCoverageConfig.ts`
 * already classifies these same paths as `home | static | terms | blog |
 * caseStudy`; consumers needing a class call `getFaqRouteFamily` rather than a
 * sixth taxonomy growing in this file.
 */

import { blogPosts } from "../data/blogPosts";
import { caseStudies } from "../data/caseStudies";
import { SEO_LANGUAGES, type SeoLanguage } from "./seoMetadata";

/**
 * The locale-less static route paths, in the order the build generators
 * declare them.
 *
 * The home page is spelled `""` rather than `"/"` so a route path can be
 * concatenated onto a locale prefix without a special case at every call site,
 * and so this array compares element-for-element against the generators'
 * `STATIC_ROUTES` when `E5.8` binds them.
 */
export const STATIC_ROUTE_PATHS = [
  "",
  "services/consulting",
  "services/fixed-price",
  "services/teams",
  "services/enablement",
  "open-source/kindly-web-search",
  "open-source/lad-code-review",
  "open-source/tdd",
  "sovereign-ai",
  "industries/defence",
  "industries/pharma",
  "industries/finance",
  "industries/enterprise",
  "insights",
  "case-studies",
  "blog",
  "terms",
  "privacy",
] as const;

/**
 * One indexable URL: the locale it serves, its route path, and the path served.
 *
 * `locale` is mandatory for every entry, the root included. An earlier draft of
 * this docstring expected `E3.3` to widen it to nullable for `/`; CWS-51
 * instead gave the root `locale: "en"` — see the module docstring for why the
 * nullable shape is refused.
 */
export type IndexableUrl = {
  /** The locale prefix, one of `SEO_LANGUAGES`. */
  locale: SeoLanguage;
  /** The locale-less route path; `""` for the home page and the root document. */
  routePath: string;
  /** The served path, locale-prefixed, leading slash, no trailing slash. */
  urlPath: string;
};

/**
 * Every locale-less route path this site intends to be indexed.
 *
 * **Order is part of the contract**, not incidental: static routes, then blog
 * posts, then case studies. `scripts/audit-faq-coverage.mjs` iterates this
 * sequence (via `faqResolver`'s re-export) and its report output follows it,
 * and the sitemap generator emits the same order, so a diff between the two
 * reads naturally.
 */
export const INDEXABLE_ROUTE_PATHS: readonly string[] = [
  ...STATIC_ROUTE_PATHS,
  ...blogPosts.map((post) => `blog/${post.slug}`),
  ...caseStudies.map((caseStudy) => `case-studies/${caseStudy.slug}`),
];

/**
 * Apply a locale prefix to a route path.
 *
 * @param locale - The locale prefix to apply.
 * @param routePath - A locale-less route path; `""` for the home page.
 * @returns The served path, with no trailing slash.
 */
function toUrlPath(locale: SeoLanguage, routePath: string): string {
  return routePath === "" ? `/${locale}` : `/${locale}/${routePath}`;
}

/**
 * Every indexable URL of the per-locale fan-out: {@link INDEXABLE_ROUTE_PATHS}
 * in every supported locale.
 *
 * This is the array the `(locale, routePath)` pair is unique over — one URL per
 * route per locale. {@link INDEXABLE_URLS} adds the root document on top,
 * which shares the home's `("en", "")` key and therefore must not enter a
 * lookup keyed by that pair.
 *
 * Unlike {@link INDEXABLE_ROUTE_PATHS}, the order here is **incidental** — it
 * falls out of iterating locales outermost, no consumer depends on it, and no
 * test pins it. Sort or group as you need.
 */
export const LOCALIZED_INDEXABLE_URLS: readonly IndexableUrl[] = SEO_LANGUAGES.flatMap(
  (locale) =>
    INDEXABLE_ROUTE_PATHS.map((routePath) => ({
      locale,
      routePath,
      urlPath: toUrlPath(locale, routePath),
    })),
);

/**
 * The bare root document (`D3`, `E3.3` / CWS-51; body since CWS-237 / `D20`).
 *
 * A **singleton**, deliberately outside the fan-out: `/` is not a per-language
 * path, so adding it as a 50th fan-out entry would mint four locales' worth of
 * paths that do not exist. The locale is `"en"` because the root document is
 * English-authored; see the module docstring for why that is not `null`.
 */
export const ROOT_INDEXABLE_URL: IndexableUrl = {
  locale: "en",
  routePath: "",
  urlPath: "/",
};

/**
 * Every indexable URL: the per-locale fan-out plus the root document — 251
 * entries today. Every SEO invariant that says "every indexable URL" means
 * every entry here.
 */
export const INDEXABLE_URLS: readonly IndexableUrl[] = [
  ...LOCALIZED_INDEXABLE_URLS,
  ROOT_INDEXABLE_URL,
];
